import * as ActionTypes from './ActionTypes'
import * as VNCFD_ActionTypes from '../VNCustomerFacingDisplay/ActionTypes'

import { VN_BRIDGE_RECEIVE_METHODS } from './Enums'
import { userPaymentProgressHandler } from './bridgeReceiveHandlers/userPaymentProgressHandler'

import { VNDataBridgeReceiveType } from './bridgeCalls/VNWebSDKDataReceive'

import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import {
  bridge_sendReceiptMessage,
  bridge_updateTransactionLifecycle,
  isScreenAcceptable,
  sendExecuteAuthRequest,
  sendExecuteRefundRequest,
  sendExecuteSalesRequest,
  sendUserPaymentProgress,
} from './bridgeCalls/VNWebSDKDataSend'
import { CFD_EVENTS, CFD_POS_PAYMENT_FLOWS, USER_PROGRESS } from '../VNCustomerFacingDisplay/Enums'
import {
  setCFDScreen,
  setOrderUpdate,
  setBalanceToBePaid,
  setCurrentPaymentFlow,
  updateTransactionLifecycle,
  setCurrentOrder,
  setPaymentData,
  setPrintStatus,
  setAvailableBalance,
  setCfdWebViewLoaded,
  saveCFDAndroidConfig,
  setCurrentMessageAsRead,
  setCartTotalsUuid,
  setCartTotals,
} from '../VNCustomerFacingDisplay/ActionCreators'
import {
  getCartTotals,
  getCartTotalsUuid,
  getCFDScreen,
  getCfdWebViewLoaded,
  getCurrentCart,
  getCurrentMessageUuid,
  getCurrentPaymentFlow,
  getPaymentData,
} from '../VNCustomerFacingDisplay/Selectors'

import { printOrder } from '../actions/printer'
import {
  addDonationsToOrderInProgress,
  addPaymentToOrderInProgress,
  addTipOrderInProgress,
  sendOrderText,
} from '../actions/order'
import { setBridgePaymentMethod, onBuildInfoReceived } from '../actions/peripheral'

import { getCardAuthStepOne, getCardAuthStepTwo } from '../VNDialogs/Selectors'
import { setCardAuthStepOne, setCardAuthStepTwo, setCFDBlockingDialog } from '../VNDialogs/ActionCreators'

import { getDeviceMode, getSummitMode } from '../VNMode/Selectors'
import { MODES } from '../VNMode/Reducer'
import { setDeviceId } from '../VNMode/ActionCreators'

import { bridgePaymentMethods } from '../reducers/peripheral'

import { getOrderInProgress } from '../selectors/order'

import { centsToDollars, stringToCents } from '../utils/formatters'
import { getDeviceSerial } from '../utils/deviceData'
import { invoiceNumber } from '../utils/orderNumbers'
import { isTabbed } from '../utils/orderStates'
import VNTransactionRecovery from '../VNTransactions/actions/VNTransactionRecovery'
import { dollarsToCents } from '../VNUtil/Money'
import { setRoute } from '../VNRoutes/ActionCreators'
import { ToastManager } from '../utils/toastManager'
import { sendEmailJob } from './Api'
import { executeSaveConfig } from '../utils/bridgeMethods'
import {
  CFDCommStates,
  configureCfdOnLoad,
  handleA3700PairingState,
  isCommStatesEnabled,
} from './bridgeReceiveHandlers/VNDevicePairingResult'
import { getTransactionBrandingConfig } from '../utils/buildSettings'
import createDataDogLog from '../utils/datadog'
import { processCFDScreenNav } from './bridgeReceiveHandlers/processCFDScreenNav'
import { setDebuggerPassthroughEnabled } from '../VNTestMode/ActionCreators'
import { getDebuggerPassthroughEnabled } from '../VNTestMode/Selectors'

const TRANSACTION_DELAY = 5000
const TRANSACTION_ERROR_DELAY = 3000

/**
 * TODO: REMOVE ALL SET TIMEOUT FUNCTIONS. To be fixed on F3678
 * Called to set receiving method name and accompanying data
 * from Native to JS via the bridge.
 * @param {object} data
 */
export function setWebSDKDataReceive(response) {
  return (dispatch, getState) => {
    const curState = getState()
    const isCommPairingMode = isCommStatesEnabled(curState)

    // pairing result post apk 2.20

    if (response.method === VNDataBridgeReceiveType.VN_CFD_COMM_STATE) {
      const cfdMode = getDeviceMode(curState)

      if (cfdMode !== MODES.POS) {
        handleA3700PairingState(curState, dispatch, response)
      }
    }

    if (response.method === VNDataBridgeReceiveType.VN_CFD_COMM_STATE && isCommPairingMode) {
      const isPaired = response.data.state === CFDCommStates.CONNECTED
      const cfdMode = getDeviceMode(curState)
      const screen = getCFDScreen(curState)
      const cartTotalsUuid = getCartTotalsUuid(curState)
      const isSummit = getSummitMode(curState)

      // only flag CFD not loaded pre-Summit since it restarts the CFD
      if (!isSummit && !isPaired) {
        dispatch(setCfdWebViewLoaded(false))
      }
      dispatch({
        type: VNCFD_ActionTypes.VN_SET_IS_PAIRED,
        isPaired,
      })
      if (cfdMode === MODES.CFD) {
        isScreenAcceptable(screen, cartTotalsUuid)
      }

      if (cfdMode === MODES.POS) {
        configureCfdOnLoad(curState, dispatch, response)
      }
      return
    }

    if (response.method === VNDataBridgeReceiveType.VN_CFD_SCREEN_NAV && response.data?.onPair === 'true') {
      processCFDScreenNav(response, dispatch, getState)
      return
    }

    // pairing result has returned for the device
    if (response.method === VNDataBridgeReceiveType.VN_DEVICE_PAIRING_RESULT) {
      // if it's apk 2.20 or higher use VN_CFD_COMM_STATE
      if (isCommPairingMode) return
      const screen = getCFDScreen(curState)
      const cartTotalsUuid = getCartTotalsUuid(curState)
      const isPaired = response.data.pairingState === 'Paired'

      dispatch({
        type: VNCFD_ActionTypes.VN_SET_IS_PAIRED,
        isPaired,
      })

      if (response.data.device !== MODES.POS) {
        isScreenAcceptable(screen, cartTotalsUuid)
      }

      if (response.data.device === MODES.POS) {
        configureCfdOnLoad(getState(), dispatch, response)
      }
    } else if (response.method === VNDataBridgeReceiveType.VN_INITIALIZE_CFD) {
      // This is the POS devices ID - the L1400 more than likely at this time.
      if (response?.data?.deviceId) {
        localStorage.setItem('device_id', response.data.deviceId)
      }
      if (response?.data?.serialNumber) {
        localStorage.setItem('serial_number', response.data.serialNumber)
      }
      if (response?.data?.invoiceNumberPrefix) {
        localStorage.setItem('invoiceNumberPrefix', response.data.invoiceNumberPrefix)
      }

      localStorage.setItem('image_version', Date.now())

      // initialize the CFD
      dispatch({
        type: VNCFD_ActionTypes.VNCFD_SET_CFD_INITIALIZED,
        data: response.data,
      })
    } else if (response.method === VNDataBridgeReceiveType.VN_UPDATE_CART) {
      // set the cart items for the CFD
      dispatch({
        type: VNCFD_ActionTypes.VNCFD_UPDATE_CART,
        cartItems: response.data.cartItems,
      })
    } else if (response.method === VNDataBridgeReceiveType.VNCFD_USER_PAYMENT_PROGRESS) {
      userPaymentProgressHandler(getState(), dispatch, response.data)
    } else if (response.method === VNDataBridgeReceiveType.VN_CFD_SCREEN_NAV) {
      const { messageUuid } = response.data

      if (messageUuid) {
        bridge_sendReceiptMessage(messageUuid)
      }

      // must set executeSalesRequest to false every time because we can set it
      // to false and it'll stay false unless added to every screen nav request
      if (response.data.executeSalesRequest) {
        dispatch({
          type: VNCFD_ActionTypes.VN_CAN_EXECUTE_SALES_REQUEST,
          data: response.data.executeSalesRequest,
        })
      } else {
        dispatch({
          type: VNCFD_ActionTypes.VN_CAN_EXECUTE_SALES_REQUEST,
          data: true,
        })
      }

      if (response.data.availableBalance) {
        dispatch(setAvailableBalance(response.data.availableBalance))
      }

      if (response.data.balanceToBePaid) {
        dispatch(setBalanceToBePaid(response.data.balanceToBePaid))
      }

      // check for other data that tells us where to store it prior to the navigation
      // if we need to update the cart prior to the screen navigation
      if (response.data.cartItems) {
        dispatch({
          type: VNCFD_ActionTypes.VNCFD_UPDATE_CART,
          cartItems: response.data.cartItems,
        })
      }

      if (response.data.formattedCartItems) {
        dispatch({
          type: VNCFD_ActionTypes.VNCFD_UPDATE_FORMATTED_CART,
          cartItems: response.data.formattedCartItems,
        })
      }

      if (response.data.cartTotals) {
        dispatch(setCartTotalsUuid(response.data.cartTotals.uuid))
        dispatch({
          type: VNCFD_ActionTypes.VNCFD_SET_CART_TOTALS,
          totals: {
            ...response.data.cartTotals,
            tax: Boolean(parseInt(response.data.cartTotals.tax)) ? parseInt(response.data.cartTotals.tax) : 0,
            subtotal: Boolean(parseInt(response.data.cartTotals.subtotal)) ? parseInt(response.data.cartTotals.subtotal) : 0,
            tip: Boolean(parseInt(response.data.cartTotals.tip)) ? parseInt(response.data.cartTotals.tip) : 0,
            serviceCharge: Boolean(parseInt(response.data.cartTotals.serviceCharge))
              ? parseInt(response.data.cartTotals.serviceCharge)
              : 0,
            discount: Boolean(parseInt(response.data.cartTotals.discount)) ? parseInt(response.data.cartTotals.discount) : 0,
            // remaining ticket balance should not be used for totaling.
            remainingTicketBalance: !isNaN(parseInt(response.data.cartTotals.remainingTicketBalance))
              ? parseInt(response.data.cartTotals.remainingTicketBalance)
              : undefined,
            charityAmountInCents: parseInt(response.data?.cartTotals?.charityAmountInCents) || 0,
            invoiceNumber: response.data.invoiceNumber,
          },
        })
      }

      if (response.data.tips) {
        dispatch({
          type: VNCFD_ActionTypes.VNCFD_TIPS_DATA,
          tips: response.data.tips,
        })
      }

      if (response.data.transaction) {
        dispatch({
          type: VNCFD_ActionTypes.VNCFD_TRANSACTION_RESULT,
          data: response.data.transaction,
        })
      }

      if (response.data.currentPaymentFlow) {
        dispatch(setCurrentPaymentFlow(response.data.currentPaymentFlow))
      }

      // set the screen
      dispatch(setCFDScreen(response.data.screen))
    } else if (
      response.method === VN_BRIDGE_RECEIVE_METHODS.VN_BRIDGE_PASSTHROUGH &&
      response.data.method === VNDataBridgeReceiveType.VN_RESET_CFD
    ) {
      // we are starting a new transaction, resest the CFD state
      dispatch({
        type: VNCFD_ActionTypes.VNCFD_RESET,
        data: response.data?.data,
      })
    } else if (response.method === VNDataBridgeReceiveType.VN_TRANSACTION_RECOVERY) {
      // Recovering from WIN Death, this contains result of last transaction
      if (!response?.data) return
      const data = JSON.parse(response?.data ?? '')

      // no data to recover with
      if (data?.status === undefined || data?.reasonCode === undefined) {
        ToastManager.error('Error processing card, please try again.')
        dispatch(setRoute('/user-login'))
        return
      }

      // Transaction Recovery Actions
      VNTransactionRecovery(dispatch, getState, data)

      return
    } else if (response.method === VNDataBridgeReceiveType.VN_TRANSACTION_RESULT) {
      const cfdMode = getDeviceMode(getState())
      const currentPaymentFlow = getCurrentPaymentFlow(getState())

      let paymentProgressObj = {
        ...USER_PROGRESS.CARD_PAYMENT.PAYMENT_FINISHED.VIEWING_RECEIPT,
      }

      // card present refunds
      if (response.data?.transType === 'refund') {
        if (cfdMode === MODES.POS) {
          // clear the blocking dialog
          dispatch(setCFDBlockingDialog(null))
          return
        }

        // no actions needed from CFD, reset CFD to idle screen
        dispatch(setCFDScreen(CFD_SCREEN.IDLE))
        return
      }

      // Approvals are A and C
      if (response.data?.receiptData?.approvalCode === 'A' || response.data?.receiptData?.approvalCode === 'C') {
        const authStepOne = getCardAuthStepOne(getState())
        const authStepTwo = getCardAuthStepTwo(getState())
        if (authStepOne?.get('open')) {
          dispatch(setCardAuthStepOne({ open: false }))
          dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeSaleRequest)) // reset the bridge payment method
          dispatch(setCardAuthStepTwo({ open: true, amount: authStepOne.get('amount') }))
        }

        dispatch({
          type: VNCFD_ActionTypes.VNCFD_TRANSACTION_RESULT,
          data: response?.data,
        })

        if (cfdMode === MODES.POS) {
          if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.TICKET || currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.GIFT_CARD) {
            const orderInProgress = getOrderInProgress(getState())

            dispatch(
              addPaymentToOrderInProgress({
                cardReaderData: response.data,
                orderNumber: orderInProgress?.orderNumber,
                tenderAmountInCents: stringToCents(response.data.amount),
                paymentId: orderInProgress?.paymentId,
                paymentType: 'freedompay_credit',
                navigateHistory: false,
              }),
            )
          } else if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.QR_PAY) {
            const orderInProgress = getOrderInProgress(getState())

            dispatch(
              addPaymentToOrderInProgress({
                cardReaderData: response.data,
                orderNumber: orderInProgress?.orderNumber,
                tenderAmountInCents: stringToCents(response.data.amount),
                paymentId: orderInProgress?.paymentId,
                paymentType: 'freedompay_credit',
                navigateHistory: false,
              }),
            )
          } else if (
            currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY ||
            currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.AUTO_SPLIT
          ) {
            // don't move on POS receiving the message from android, lets CFD complete and return to the web first
            const orderInProgress = getOrderInProgress(getState())
            // when first split pay payment is done get order in progress doesn't exist.
            // Use get current cart to gather the required data so the orders saga doesn't create more variables.
            const currentCart = getCurrentCart(getState())

            dispatch(
              addPaymentToOrderInProgress({
                cardReaderData: response.data,
                orderNumber: orderInProgress?.orderNumber ? orderInProgress?.orderNumber : currentCart?.get('orderNumber'),
                tenderAmountInCents: stringToCents(response.data.amount),
                paymentId: orderInProgress?.paymentId,
                paymentType: 'freedompay_credit',
                navigateHistory: false,
              }),
            )
          } else {
            const orderInProgress = getOrderInProgress(getState())
            if ((isTabbed(orderInProgress) || orderInProgress.isTab) && !(authStepOne?.get('open') || authStepTwo?.get('open'))) {
              // when first split pay payment is done get order in progress doesn't exist.
              // Use get current cart to gather the required data so the orders saga doesn't create more variables.
              const currentCart = getCurrentCart(getState())

              dispatch(
                addPaymentToOrderInProgress({
                  cardReaderData: response.data,
                  orderNumber: orderInProgress?.orderNumber ? orderInProgress?.orderNumber : currentCart?.get('orderNumber'),
                  tenderAmountInCents: stringToCents(response.data.amount),
                  paymentId: orderInProgress?.paymentId,
                  paymentType: 'freedompay_credit',
                  navigateHistory: false,
                }),
              )
            }

            dispatch({
              type: VNCFD_ActionTypes.VNCFD_USER_PAYMENT_PROGRESS,
              data: paymentProgressObj,
            })
          }
        } else if (cfdMode === MODES.CFD) {
          const paymentData = getPaymentData(getState())
          // dispatch transaction result again because the transaction result only has the amount paid and not the
          // full order total
          if (paymentData?.get('orderTotal')) {
            dispatch({
              type: VNCFD_ActionTypes.VNCFD_TRANSACTION_RESULT,
              data: {
                amount: paymentData.get('orderTotal'),
              },
            })
          }
          if (
            currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION &&
            currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.SPLIT_PAY &&
            currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.QR_PAY
          ) {
            dispatch({
              type: VNCFD_ActionTypes.VNCFD_SET_CART_TOTALS,
              totals: {
                uuid: paymentData.get('uuid'),
              },
            })

            dispatch(setCFDScreen(CFD_SCREEN.RECEIPT))
          }

          if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION) {
            dispatch(setCFDScreen(CFD_SCREEN.IDLE))
          }

          // can't let POS decide since broadPOS doesn't release quick enough, CFD needs to decide
          if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY || currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.AUTO_SPLIT) {
            // if the time isn't here, then the bridge won't be active on the way BACK to the CFD from the POS.
            // THIS message will go OUT to the POS, but the subsequent call from POS won't ever reach CFD. Strange...
            setTimeout(() => {
              bridge_updateTransactionLifecycle(CFD_EVENTS.CC_PAYMENT_COMPLETE)
            }, TRANSACTION_DELAY)

            if (paymentData?.get('isFinalPayment') === 'true') {
              dispatch({
                type: VNCFD_ActionTypes.VNCFD_SET_CART_TOTALS,
                totals: {
                  uuid: paymentData.get('uuid'),
                },
              })

              dispatch(setCFDScreen(CFD_SCREEN.RECEIPT))
            }
          }
        }
        // approved offline transactions
      } else if (response.data?.isOfflineApproval) {
        const authStepOne = getCardAuthStepOne(getState())
        const authStepTwo = getCardAuthStepTwo(getState())
        if (authStepOne?.get('open')) {
          dispatch(setCardAuthStepOne({ open: false }))
          dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeSaleRequest)) // reset the bridge payment method
          dispatch(setCardAuthStepTwo({ open: true, amount: authStepOne.get('amount') }))
        }

        dispatch({
          type: VNCFD_ActionTypes.VNCFD_TRANSACTION_RESULT,
          data: response?.data,
        })

        if (cfdMode === MODES.POS) {
          if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.QR_PAY) {
            const orderInProgress = getOrderInProgress(getState())

            dispatch(
              addPaymentToOrderInProgress({
                cardReaderData: response.data,
                orderNumber: orderInProgress?.orderNumber,
                tenderAmountInCents: stringToCents(response.data.amount),
                paymentId: orderInProgress?.paymentId,
                paymentType: 'freedompay_credit',
                navigateHistory: false,
                isApprovedOffline: true,
              }),
            )
          } else if (
            currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY ||
            currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.AUTO_SPLIT
          ) {
            const orderInProgress = getOrderInProgress(getState())

            dispatch(
              addPaymentToOrderInProgress({
                cardReaderData: response.data,
                orderNumber: orderInProgress?.orderNumber,
                tenderAmountInCents: stringToCents(response.data.amount),
                paymentId: orderInProgress?.paymentId,
                paymentType: 'freedompay_credit',
                navigateHistory: false,
                isApprovedOffline: true,
              }),
            )
          } else if (
            currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.TICKET ||
            currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.GIFT_CARD
          ) {
            const orderInProgress = getOrderInProgress(getState())

            dispatch(
              addPaymentToOrderInProgress({
                cardReaderData: response.data,
                orderNumber: orderInProgress?.orderNumber,
                tenderAmountInCents: stringToCents(response.data.amount),
                paymentId: orderInProgress?.paymentId,
                paymentType: 'freedompay_credit',
                navigateHistory: false,
                isApprovedOffline: true,
              }),
            )
          } else {
            const orderInProgress = getOrderInProgress(getState())
            const isFixedCustomTenderPayment = orderInProgress?.payments?.[0]?.userAttributes?.isFixedCustomTender

            if (
              (isTabbed(orderInProgress) || orderInProgress.isTab || isFixedCustomTenderPayment) &&
              !(authStepOne?.get('open') || authStepTwo?.get('open'))
            ) {
              const currentCart = getCurrentCart(getState())

              dispatch(
                addPaymentToOrderInProgress({
                  cardReaderData: response.data,
                  orderNumber: orderInProgress?.orderNumber ? orderInProgress?.orderNumber : currentCart?.get('orderNumber'),
                  tenderAmountInCents: stringToCents(response.data.amount),
                  paymentId: orderInProgress?.paymentId,
                  paymentType: 'freedompay_credit',
                  navigateHistory: false,
                  isApprovedOffline: true,
                }),
              )
            }

            dispatch({
              type: VNCFD_ActionTypes.VNCFD_USER_PAYMENT_PROGRESS,
              data: paymentProgressObj,
            })
          }
        } else if (cfdMode === MODES.CFD) {
          if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY || currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.AUTO_SPLIT) {
            // if the time isn't here, then the bridge won't be active on the way BACK to the CFD from the POS.
            // THIS message will go OUT to the POS, but the subsequent call from POS won't ever reach CFD. Strange...
            setTimeout(() => {
              bridge_updateTransactionLifecycle(CFD_EVENTS.CC_PAYMENT_COMPLETE)
            }, TRANSACTION_DELAY)
          } else {
            const paymentData = getPaymentData(getState())
            // dispatch transaction result again because the transaction result only has the amount paid and not the
            // full order total
            if (paymentData?.get('orderTotal')) {
              dispatch({
                type: VNCFD_ActionTypes.VNCFD_TRANSACTION_RESULT,
                data: {
                  amount: paymentData?.get('orderTotal'),
                },
              })
            }

            if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION) {
              dispatch(setCFDScreen(CFD_SCREEN.IDLE))
            } else {
              dispatch(setCFDScreen(CFD_SCREEN.RECEIPT))
            }
          }
        }
      } else {
        const words = response.data?.reasonText?.split(' ')

        const responseText = words
          ?.map((word) => {
            return word[0]?.toUpperCase() + word?.substring(1)?.toLowerCase()
          })
          .join(' ')

        // this is a declined card from BroadPOS App
        const error = {
          title: USER_PROGRESS.CARD_PAYMENT.ERROR.title,
          error: true,
          subTitle: `${responseText}\n Error Code: ${response.data.reasonCode}`,
          flow: 'TRANSACTION_ERROR',
        }

        if (cfdMode === MODES.CFD) {
          setTimeout(() => {
            sendUserPaymentProgress(error)
          }, TRANSACTION_ERROR_DELAY)
        }

        dispatch(setCFDScreen(CFD_SCREEN.CARD_DECLINED))

        const CFDerror = {
          title: `${responseText}\n Error Code: ${response.data.reasonCode}`,
          subTitle: 'Please wait for cashier.',
        }

        dispatch({
          type: VNCFD_ActionTypes.VNCFD_CARD_ERROR,
          data: CFDerror,
        })
      }
    } else if (response.method === VNDataBridgeReceiveType.VN_PRINT_RECEIPT) {
      if (response.data.emails) {
        sendEmailJob({ orderUuid: response.data.orderUuid, emails: response.data.emails })
      } else if (response.data.phone) {
        dispatch(sendOrderText(response.data.orderUuid, response.data.phone))
      } else if (response.data.TEMP_activateCardReader) {
        const cartTotals = getCartTotals(getState())

        // pick up hijacked request - i'm on the CFD
        let currentPaymentData = {
          amount: parseInt(response.data.total) / 100,
          tip: 0,
          hRef: response?.data?.orderNumber
            ? invoiceNumber(response?.data?.orderNumber)
            : invoiceNumber(cartTotals?.get('orderNumber')),
          orderNumber: response?.data?.orderNumber ? response?.data?.orderNumber : cartTotals?.get('orderNumber'),
          uuid: response.data?.uuid || cartTotals?.get('uuid'),
          deviceSerial: getDeviceSerial(),
          ...getTransactionBrandingConfig(),
          orderTotal: parseInt(response.data.orderTotal) / 100,
          isFinalPayment: response.data?.isFinalPayment, // this data is fine to send to android since android only uses the data it needs
          // and ignores the rest
        }
        dispatch(setPaymentData(currentPaymentData))
        dispatch(setCartTotals({ ...cartTotals.toObject(), TEMP_activateCardReader: true }))
        dispatch(setCFDScreen(CFD_SCREEN.PROCESSING))
        sendExecuteSalesRequest(currentPaymentData)
      } else {
        // we are on the POS, need to send this through the old bridge to print
        dispatch(printOrder({ orderUuid: response.data.orderUuid }))
      }
    } else if (response.method === VNDataBridgeReceiveType.RETRY_PAYMENT) {
      const cartTotals = getCartTotals(getState())
      const paymentData = getPaymentData(getState())
      const curUuid = cartTotals?.get('uuid')
      const curTip = cartTotals?.get('tip')
      const curCharityAmount = cartTotals?.get('charityAmountInCents')
      const curTotal = parseInt(cartTotals?.get('total')) + parseInt(curTip) + parseInt(curCharityAmount)
      const shouldUsePaymentData = cartTotals?.get('TEMP_activateCardReader') || !curUuid

      // retry payment data should come from cartTotals, falling back on paymentData
      const curData = shouldUsePaymentData
        ? paymentData.toObject()
        : {
            amount: centsToDollars(curTotal),
            deviceSerial: getDeviceSerial(),
            hRef: cartTotals?.get('invoiceNumber'),
            ...getTransactionBrandingConfig(),
            orderNumber: cartTotals?.get('orderNumber'),
            orderTotal: curTotal / 100,
            tip: centsToDollars(parseInt(curTip)),
            tipAmount: curTip,
            uuid: curUuid,
            charityAmountInCents: parseInt(curCharityAmount) / 100,
          }

      if (response.data?.bridgePaymentMethod === bridgePaymentMethods.executeAuthRequest) {
        sendExecuteAuthRequest(curData)
      } else {
        sendExecuteSalesRequest(curData)
      }

      sendUserPaymentProgress({ ...USER_PROGRESS.CARD_PAYMENT.PAYING_CARD, amount: centsToDollars(curTotal) })

      // move to the processing screen
      dispatch(setCFDScreen(CFD_SCREEN.PROCESSING))
    } else if (response.method === VNDataBridgeReceiveType.VN_ORDER_UPDATE) {
      dispatch(setOrderUpdate(response.data))

      // handle case where app crashes on L1400, but remains open on A3700
      const isCfdWebViewLoaded = getCfdWebViewLoaded(curState)
      if (!isCfdWebViewLoaded) {
        dispatch(setCfdWebViewLoaded(true))
      }
    } else if (response.method === VNDataBridgeReceiveType.VN_DEVICE_ID) {
      dispatch(setDeviceId(response.data.deviceId))
    } else if (response.method === VN_BRIDGE_RECEIVE_METHODS.VN_BRIDGE_PASSTHROUGH) {
      if (response.data.method === VNDataBridgeReceiveType.VN_EXECUTE_AUTH_REQUEST) {
        const data = { ...response.data.data }
        // [T356525] CFD - zero-dollar tab as one-dollar auth
        if (dollarsToCents(data.amount) === 0) {
          data.amount = '1.00'
        }

        dispatch(setPaymentData(response.data.data))
        dispatch(setCFDScreen(CFD_SCREEN.PROCESSING))
        sendExecuteAuthRequest(data)
      }

      if (response.data.method === VNDataBridgeReceiveType.VN_RECEIPT_MESSAGE) {
        const currentMessageId = getCurrentMessageUuid(getState())
        const messageUuid = response.data.uuid
        if (currentMessageId !== messageUuid) return

        dispatch(setCurrentMessageAsRead())
      }

      if (response.data.method === VNDataBridgeReceiveType.VN_CFD_WEB_VIEW_LOADED) {
        const isLoaded = response.data.data === 'true'
        dispatch(setCfdWebViewLoaded(isLoaded))
        if (isLoaded) {
          configureCfdOnLoad(getState(), dispatch, response)
        }
      }

      switch (response.data.method) {
        case VN_BRIDGE_RECEIVE_METHODS.VN_UPDATE_TRANSACTION_LIFECYCLE:
          dispatch(updateTransactionLifecycle(response.data.data.moment))
          break
        case VN_BRIDGE_RECEIVE_METHODS.VN_SET_CURRENT_ORDER:
          dispatch(addTipOrderInProgress(parseInt(response.data.data.tip)))

          if (response.data.data?.charityAmountInCents > 0) {
            dispatch(addDonationsToOrderInProgress(parseInt(response.data.data.charityAmountInCents)))
          }
          dispatch(setCurrentOrder(response.data.data))
          break
        case VN_BRIDGE_RECEIVE_METHODS.VN_SET_CURRENT_PAYMENT_FLOW:
          dispatch(setCurrentPaymentFlow(response.data.data.flow))
          break
        case VN_BRIDGE_RECEIVE_METHODS.VN_UPDATE_CFD_VERSION:
          const deviceMode = getDeviceMode(getState())
          const isSummit = getSummitMode(getState())

          let endOfUrl = ''

          if (deviceMode === MODES.CFD) {
            endOfUrl += `&cfd=true`
          }

          if (isSummit) {
            endOfUrl += `&summit=true`
          }

          window.location = `${window.location.origin}/?cacheBuster=${new Date().toISOString()}${endOfUrl}`
          break
        case VNDataBridgeReceiveType.VN_SEND_DEVICE_CONFIG:
          createDataDogLog('info', `Saving device config on A3700`, { config: response.data.data })
          executeSaveConfig(response.data.data)
          dispatch(saveCFDAndroidConfig(response.data.data))
          break
        case VNDataBridgeReceiveType.VN_PRINT_STATUS:
          dispatch(setPrintStatus(response.data.data))
          break
        case VNDataBridgeReceiveType.VN_EXECUTE_REFUND_REQUEST:
          sendExecuteRefundRequest(response.data.data)
          break
        case VNDataBridgeReceiveType.VN_ENABLE_LOG_PASSTHROUGH:
          dispatch(setDebuggerPassthroughEnabled(response.data.data))
          break
        case VNDataBridgeReceiveType.VN_LOG_PASSTHROUGH:
          const curState = getState()
          const isDebuggerPassthroughEnabled = getDebuggerPassthroughEnabled(curState)
          if (isDebuggerPassthroughEnabled) {
            // eslint-disable-next-line no-console
            console.log('%cCFD%c log:', 'color: red; font-weight: bold;', 'color: gray;', ...response.data.data)
          }
          break
        default:
          break
      }
    }

    if (response.method === VNDataBridgeReceiveType.VN_VOID_TRANSACTION) {
      // response from `Attempting to void transaction` needs no action
      return
    }

    if (response.method === VNDataBridgeReceiveType.VN_BUILD_INFO) {
      dispatch(onBuildInfoReceived(response.data))
      return
    }
  }
}

/**
 * Semantic version string of native SDK being utilized
 * @param {string} version
 */
export function setWebSDKVersion(version) {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.VNANDROIDSDK_SET_VERSION,
      version: version,
    })
  }
}

/**
 * @param {boolean} isActive set keyboard active if keyboard is active on the device
 */
export function setIsKeyboardActive(isActive) {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.VNANDROIDSDK_IS_KEYBOARD_ACTIVE,
      isActive,
    })
  }
}
